<template lang="pug">
main.container.py-14(v-if="selectedPost")
  nav.nav-breadcrumb.text-gray-700.flex.items-center
    HomeIcon.h-4.w-4
    ChevronRightIcon.h-4.w-4
    template(v-if="route.query.referrer === 'press-releases'")
      NuxtLink(to="/press-releases") Press Releases
      ChevronRightIcon.h-4.w-4
    span(@click.prevent.default="trackCompanyLinkSelected")
      NuxtLink(:to="{ name: 'companies-id', params: { id: companyUrlSlugOrId } }") {{ selectedPost.company.name }}
    ChevronRightIcon.h-4.w-4
    | {{ formatBreadcrumbDate(selectedPost.date) }}

  h2.text-5xl.mt-20.mb-6.font-bold.leading-snug {{ selectedPost.title }}
  .h5.text-xl.font-medium {{ formatDate(selectedPost.date) }} {{ selectedPost.author ? ' | ' + selectedPost.author : '' }}

  .share.my-8.flex.items-center
    share-network(network="facebook" v-analytics="'newsPostFacebookShareSelected'" :url="currentRoute" :title="selectedPost.title")
      FacebookBlack.w-10.h-10
    share-network(network="twitter" v-analytics="'newsPostTwitterShareSelected'" :url="currentRoute" :title="selectedPost.title")
      TwitterBlack.w-10.h-10
    share-network(network="linkedin" v-analytics="'newsPostLinkedInShareSelected'" :url="currentRoute" :title="selectedPost.title")
      LinkedInBlack.w-10.h-10

  .image(v-if="selectedPost.image")
    img(:src="selectedPost.image" :alt="selectedPost.title")

  article.text-lg
    iframe.mb-8(
      v-if="selectedPost.deck_id && selectedPost.deck && selectedPost.deck.view_access_type === 'public'"
      width="100%"
      height="500px"
      frameborder="0"
      allowfullscreen="true"
      allow="fullscreen"
      :src="'/embed/decks/' + selectedPost.deck_id"
    )

    div(v-if="selectedPost.content" v-html="selectedPost.content")

    .mt-2(v-if="selectedPost.attachments && selectedPost.attachments[0]")
      p Read the full article here:
      a(v-analytics="'newsPostFullArticleSelected'" :href="selectedPost.attachments[0].url" target="_blank") {{ selectedPost.attachments[0].url }}

  .text-center.mt-36
    VButton(:component="NuxtLink" to="/press-releases" variant="dark" v-analytics="'newsPostBackToNewsfeedSelected'") Press Releases
</template>

<script lang="ts" setup>
import type { Ref } from 'vue'
import type { NewsfeedPost } from '@/entities/newsfeedPost'
import { HomeIcon, ChevronRightIcon } from '@heroicons/vue/24/solid'
import FacebookBlack from '@/assets/images/social-icons/FacebookBlack.svg'
import TwitterBlack from '@/assets/images/social-icons/TwitterBlack.svg'
import LinkedInBlack from '@/assets/images/social-icons/LinkedInBlack.svg'
import { formatDateFull, formatDateShort } from '@/lib/datetime-format-utils'

const NuxtLink = resolveComponent('NuxtLink')
const { $analytics } = useNuxtApp()
const route = useRoute()

const selectedPost: Ref<NewsfeedPost | null> = ref(null)
const currentRoute = computed(() => window.location.origin + window.location.pathname)
const companyUrlSlugOrId = computed(() => selectedPost.value?.company.url_slug || selectedPost.value?.company.id)

onMounted(async () => {
  $analytics.track('showedScreenNewsPost')

  try {
    const data = await $fetch<NewsfeedPost>(`/v2/posts/${route.params.id}`, {
      method: 'GET',
      params: { include: ['attachments', 'company', 'deck'] },
      ...useFetchSharedOptions(),
    })

    selectedPost.value = data
  } catch (error) {
    if (error instanceof Error) {
      showError(error)
    }
  }
})

const trackCompanyLinkSelected = () => {
  $analytics.track('newsPostCompanyLinkSelected', {
    companyName: selectedPost.value?.company.name,
    companyId: selectedPost.value?.company.id,
    postName: selectedPost.value?.title,
    postId: selectedPost.value?.id,
  })
}

const formatBreadcrumbDate = formatDateShort
const formatDate = formatDateFull
</script>

<style lang="stylus" scoped>
nav.nav-breadcrumb
  & > *
    @apply mr-2
  svg
    @apply text-gray-500 -mt-1
  a
    color inherit
.share > *:not(:last-child)
  @apply mr-6
.share img
  height 40px
  cursor pointer
article :deep(*)
  @apply font-inter !important

  ul
    @apply mb-4
  p
    @apply mb-4
  table
    @apply max-w-fit overflow-x-auto whitespace-nowrap block
  td
    @apply text-xs p-2
    border-left: 1px solid #d8d8d8;

    &:first-child
      border-left: none

@screen md
  article :deep(*) td
    @apply text-sm p-4

@screen lg
  article :deep(*) td
    @apply text-base p-6
</style>
